import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { login } from '../api/authService';
import { useAuth } from '../auth/AuthContext';
import { useToast } from '../components/Toast';
import {
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';

function LoginPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { login: setSession, isAuthenticated } = useAuth();
  const [form, setForm] = useState({ email: '', password: '' });
  const [formErrors, setFormErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const validate = () => {
    const nextErrors = {};
    const email = form.email.trim();
    const password = form.password;

    if (!email) {
      nextErrors.email = 'Email is required.';
    } else if (!/^\S+@\S+\.\S+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (!password) {
      nextErrors.password = 'Password is required.';
    } else if (password.length < 6) {
      nextErrors.password = 'Password must be at least 6 characters.';
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: '' }));
    setError('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      toast.warning('Please fix the highlighted fields and try again.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const data = await login({
        email: form.email.trim(),
        password: form.password,
      });
      if (data?.data?.token) {
        toast.success('Login successful. Welcome back!');
        setSession(data.data.token);
        navigate('/dashboard', { replace: true });
      } else {
        const message = 'Unexpected login response from server.';
        setError(message);
        toast.error(message);
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Login failed.';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-wrap login-page">
      <div className="login-shell">
        <section className="login-brand-panel" aria-hidden="true">
          <img className="login-brand-logo" src="/PRNB_Logo.png" alt="PRNB" />
          <p className="login-brand-kicker">Lead Operations Suite</p>
          <h1>Manage assignments, queues, and SLA outcomes in one place.</h1>
          <p className="login-brand-copy">
            PRNB LMS Admin centralizes routing decisions, worklist ownership, and operational visibility for faster conversions.
          </p>
          <ul className="login-brand-points">
            <li>Rule-driven assignment engine</li>
            <li>Queue dispatch and auto-claim workflows</li>
            <li>Live dashboard with SLA risk insights</li>
          </ul>
        </section>

        <form className="auth-card login-card" onSubmit={onSubmit}>
          {/* <img className="login-logo" src="/PRNB_Logo.png" alt="PRNB" /> */}
          <h2>Sign in</h2>
          <p className="auth-subtitle">Use your LMS account to continue.</p>
          <p className="auth-try-line">
            New to PRNB LMS? <Link to="/signup">Sign up to try it yourself.</Link>
          </p>

          <label className="auth-label" htmlFor="login-email">
            Email
            <input
              id="login-email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              autoComplete="email"
              required
            />
          </label>
          {formErrors.email ? <p className="error-text">{formErrors.email}</p> : null}

          <label className="auth-label" htmlFor="login-password">
            Password
            <div className="password-field">
              <input
                id="login-password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={onChange}
                autoComplete="current-password"
                required
              />
              <button
                className="toggle-password-btn"
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </label>
          {formErrors.password ? <p className="error-text">{formErrors.password}</p> : null}
          {error ? <p className="error-text">{error}</p> : null}
          <button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
          <p className="auth-footer-text">
            New here? <Link to="/signup">Create an account</Link>
          </p>
        </form>
      </div>
    </div>
  );
}

export default LoginPage;
